import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0B0F1A]/85 backdrop-blur-xl border-b border-[#1E293B]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-7 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="navbar-brand-link"
          className="flex items-center gap-2 group"
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-[#4DA3FF]/15 border border-[#4DA3FF]/30">
            <Sparkles className="h-4 w-4 text-[#4DA3FF]" strokeWidth={1.8} />
            <span className="absolute inset-0 rounded-lg bg-[#4DA3FF]/30 blur-xl opacity-0 group-hover:opacity-100 transition" />
          </span>
          <span className="font-display text-[17px] font-medium tracking-tight">
            WebAura<span className="text-[#4DA3FF]">Sites</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="navbar-desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive
                    ? "text-white bg-[#1A2235]"
                    : "text-[#94A3B8] hover:text-white hover:bg-[#111827]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="navbar-cta-button"
            className="btn-primary text-sm px-5 py-2.5"
          >
            Get a Website
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#1E293B] text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-[#1E293B] bg-[#0B0F1A]"
        >
          <div className="container-7 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base ${
                    isActive
                      ? "text-white bg-[#1A2235]"
                      : "text-[#94A3B8] hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="btn-primary mt-3 w-full"
            >
              Get a Website
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
